import { ChoiceRow, HIDDEN_INPUT, PEER_FOCUS_RING, choiceAria, choiceIds } from "./ChoiceRow";
import { cn } from "@/lib/cn";
import { cva } from "class-variance-authority";
import { forwardRef, useId, type ComponentPropsWithoutRef, type ReactNode } from "react";

const trackVariants = cva(
  "relative inline-flex shrink-0 items-center rounded-full p-0.5 transition-colors duration-base ease-standard",
  {
    variants: {
      size: { sm: "h-5 w-9", md: "h-6 w-11" },
      tone: { primary: "", secondary: "" },
      checked: { true: "", false: "bg-card-text/20" },
      disabled: { true: "opacity-50", false: "" },
    },
    compoundVariants: [
      { checked: true, tone: "primary", class: "bg-primary" },
      { checked: true, tone: "secondary", class: "bg-secondary" },
    ],
    defaultVariants: { size: "md", tone: "primary", checked: false, disabled: false },
  },
);

const THUMB_SIZE = { sm: "h-4 w-4", md: "h-5 w-5" } as const;
const THUMB_ON = { sm: "translate-x-4", md: "translate-x-5" } as const;

export type SwitchProps = Omit<ComponentPropsWithoutRef<"input">, "type" | "size" | "onChange" | "checked"> & {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label?: ReactNode;
  description?: ReactNode;
  tone?: "primary" | "secondary";
  size?: "sm" | "md";
  /** Where the switch sits relative to the text; settings rows put it at the end. */
  indicatorPosition?: "start" | "end";
};

/** On/off control: a native checkbox announced as `role="switch"` behind a sliding track. */
export const Switch = forwardRef<HTMLInputElement, SwitchProps>(function Switch(
  {
    checked,
    onCheckedChange,
    label,
    description,
    tone = "primary",
    size = "md",
    indicatorPosition = "end",
    className,
    disabled,
    ...rest
  },
  ref,
) {
  const ids = choiceIds(useId());
  return (
    <ChoiceRow
      size={size}
      disabled={disabled}
      label={label}
      description={description}
      ids={ids}
      indicatorPosition={indicatorPosition}
      className={className}
      input={
        <input
          {...rest}
          ref={ref}
          type="checkbox"
          role="switch"
          checked={checked}
          aria-checked={checked}
          disabled={disabled}
          {...choiceAria(ids, label, description, rest)}
          onChange={(event) => onCheckedChange(event.target.checked)}
          className={HIDDEN_INPUT}
        />
      }
      indicator={
        <span aria-hidden className={cn(trackVariants({ size, tone, checked, disabled }), PEER_FOCUS_RING)}>
          <span
            className={cn(
              "rounded-full bg-card shadow-raised transition-transform duration-base ease-standard",
              THUMB_SIZE[size],
              checked ? THUMB_ON[size] : "translate-x-0",
            )}
          />
        </span>
      }
    />
  );
});
